import moment from 'moment';
import {
   FOOTNOTES_CATEGORIES,
   RULES_CATEGORIES,
   RECORD_3_CATEGORIES_WITH_NESTED_FIELDS,
   NESTED_RECORD_3_ARRAYS,
} from '../../../utils/constants';
import { DateUtil } from '../../../utils/date-util';
import {
   CATEGORY_11_TABLE_HEADERS,
   CATEGORY_14_TABLE_HEADERS,
   CATEGORY_15_TABLE_HEADERS,
   CATEGORY_3_TABLE_HEADERS,
   CATEGORY_01_TABLE_HEADERS,
   CATEGORY_02_TABLE_HEADERS,
   CATEGORY_04_TABLE_HEADERS,
   CATEGORY_05_TABLE_HEADERS,
   CATEGORY_06_TABLE_HEADERS,
   CATEGORY_07_TABLE_HEADERS,
   CATEGORY_08_TABLE_HEADERS,
   CATEGORY_09_TABLE_HEADERS,
   CATEGORY_12_TABLE_HEADERS,
   CATEGORY_13_TABLE_HEADERS,
   CATEGORY_16_TABLE_HEADERS,
   CATEGORY_17_TABLE_HEADERS,
   CATEGORY_18_TABLE_HEADERS,
   CATEGORY_19_TABLE_HEADERS,
   CATEGORY_20_TABLE_HEADERS,
   CATEGORY_21_TABLE_HEADERS,
   CATEGORY_22_TABLE_HEADERS,
   CATEGORY_23_TABLE_HEADERS,
   CATEGORY_26_TABLE_HEADERS,
   CATEGORY_27_TABLE_HEADERS,
   CATEGORY_28_TABLE_HEADERS,
   CATEGORY_29_TABLE_HEADERS,
   CATEGORY_31_TABLE_HEADERS,
   CATEGORY_33_TABLE_HEADERS,
   CATEGORY_35_TABLE_HEADERS,
   CATEGORY_50_TABLE_HEADERS,
} from './tableHeaders';

function validateEmptyValues(value) {
   const isArray = Array.isArray(value);
   return isArray ? value.join(',').length > 0 : value.length > 0;
}

function appendCamelCaseStrings(prefix, string) {
   if (!prefix || !string) return string;

   if(string.includes(prefix)) return string;

   const mergedString = prefix + string.charAt(0).toUpperCase() + string.slice(1);
   return mergedString;
}

const getOperatorValue = (operator) => (operator === '==' ? '=' : operator);

function getRecord2Payload(queryObject) {
   const record2Section = queryObject?.find(
      (section) => section.categoryName === 'Record 2'
   );

   const record2QueryFields = record2Section?.categoryDetails?.filter(
      ({ values }) => validateEmptyValues(values)
   );

   const record2Payload = record2QueryFields.map((field) => {
      return {
         key: field.key,
         value: field.values.join(','),
         operator: getOperatorValue(field.operator),
      };
   });

   const allCategoryNumberFields = record2Payload.filter(
      ({ key }) => key === 'categoryNumber'
   );

   if (allCategoryNumberFields.length > 1) {
      const deDuplicatedCategoryNumber = [
         ...new Set(
            allCategoryNumberFields.flatMap(({ value }) => value.split(','))
         ),
      ].join(',');

      const remainingRecord2Fields = record2Payload.filter(
         ({ key }) => key !== 'categoryNumber'
      );

      return [
         ...remainingRecord2Fields,
         { ...allCategoryNumberFields[0], value: deDuplicatedCategoryNumber },
      ];
   }

   return record2Payload;
}

function getTableQueryPayload(queryObject) {
   const tableQuery = queryObject
      ?.find((section) => section.categoryName === 'Data')
      ?.categoryDetails?.find((field) => field.value)
      ?.key?.toLowerCase();
   return tableQuery;
}

function getRecord3Payload(searchRequest) {
   const RECORD_3_SECTIONS =
      searchRequest.queryScreen === 'Footnotes'
         ? FOOTNOTES_CATEGORIES
         : RULES_CATEGORIES;

   const record3Sections = searchRequest.queryData?.filter((section) =>
      RECORD_3_SECTIONS.includes(section.categoryCode)
   );

   const record3Payload = record3Sections?.flatMap((section) => {
      const sectionQueryFields = section.categoryDetails?.filter(({ values }) =>
         validateEmptyValues(values)
      );

      const record3QueryFields = sectionQueryFields?.map((field) => {
         return {
            key: field.key,
            value: field.values.join(','),
            operator: getOperatorValue(field.operator),
            categoryNumber: parseInt(section.categoryCode),
         };
      });

      return record3QueryFields;
   });
   return record3Payload;
}

function getRecord2SegmentsPayload(queryObject) {
   const record2SegmentsSection = queryObject?.find(
      (section) => section.categoryName === 'Record 2 Segments'
   );

   const record2SegmentsQueryFields =
      record2SegmentsSection?.categoryDetails?.filter(({ values }) =>
         validateEmptyValues(values)
      );

   const record2SegmentsPayload = record2SegmentsQueryFields.map((field) => {
      return {
         key: field.key,
         value: field.values.join(','),
         operator: getOperatorValue(field.operator),
      };
   });

   return record2SegmentsPayload;
}

export function createApiPayload(searchRequest) {
   const tableQuery = getTableQueryPayload(searchRequest.queryData);
   const record2Payload = getRecord2Payload(searchRequest.queryData);
   const record3Payload = getRecord3Payload(searchRequest);
   const record2SegmentsPayload = getRecord2SegmentsPayload(
      searchRequest.queryData
   );

   const apiPayload = {
      record2Type: searchRequest.queryScreen === 'Rules' ? 'R' : 'F',
      productType: 'I',
      pageToken: null,
      bqJobInfo: null,
      ...(tableQuery && { table: { [tableQuery]: true } }),
      ...(record2Payload?.length && { record2s: record2Payload }),
      ...(record2SegmentsPayload?.length && {
         record2Segments: record2SegmentsPayload,
      }),
      ...(record3Payload?.length && { record3s: record3Payload }),
   };

   return apiPayload;
}

export const getRecord3DetailsTableHeaders = (record3Details) => {
   const headerMap = {
      1: CATEGORY_01_TABLE_HEADERS,
      2: CATEGORY_02_TABLE_HEADERS,
      3: CATEGORY_3_TABLE_HEADERS,
      4: CATEGORY_04_TABLE_HEADERS,
      5: CATEGORY_05_TABLE_HEADERS,
      6: CATEGORY_06_TABLE_HEADERS,
      7: CATEGORY_07_TABLE_HEADERS,
      8: CATEGORY_08_TABLE_HEADERS,
      9: CATEGORY_09_TABLE_HEADERS,
      11: CATEGORY_11_TABLE_HEADERS,
      12: CATEGORY_12_TABLE_HEADERS,
      13: CATEGORY_13_TABLE_HEADERS,
      14: CATEGORY_14_TABLE_HEADERS,
      15: CATEGORY_15_TABLE_HEADERS,
      16: CATEGORY_16_TABLE_HEADERS,
      17: CATEGORY_17_TABLE_HEADERS,
      18: CATEGORY_18_TABLE_HEADERS,
      19: CATEGORY_19_TABLE_HEADERS,
      20: CATEGORY_20_TABLE_HEADERS,
      21: CATEGORY_21_TABLE_HEADERS,
      22: CATEGORY_22_TABLE_HEADERS,
      23: CATEGORY_23_TABLE_HEADERS,
      26: CATEGORY_26_TABLE_HEADERS,
      27: CATEGORY_27_TABLE_HEADERS,
      28: CATEGORY_28_TABLE_HEADERS,
      29: CATEGORY_29_TABLE_HEADERS,
      31: CATEGORY_31_TABLE_HEADERS,
      33: CATEGORY_33_TABLE_HEADERS,
      35: CATEGORY_35_TABLE_HEADERS,
      50: CATEGORY_50_TABLE_HEADERS,
   };
   return headerMap[record3Details?.categoryNumber] || [];
};

const getNestedFields = (categoryNumber) => {
   const nestedKeyMap = {
      dateOverride994: {
         dateOverride994Id: 'recordId',
         travelEffectiveDate: 'travelEffectiveDate',
         travelDiscontinueDate: 'travelDiscontinueDate',
         reservationEffectiveDate: 'reservationEffectiveDate',
         reservationDiscontinueDate: 'reservationDiscontinueDate',
      },
      text996: {
         text996: 'text996Id',
         lineText: 'lineText',
      },
   };

   const nested995FieldConfig = [
      {
         fieldName: 'geoSpecification995',
         categories: [2, 3, 5, 12, 14, 16, 19, 20, 21, 22],
         prefix: 'geo995',
      },
      {
         fieldName: 'localeGeographic995',
         categories: [28],
         prefix: 'localeGeo995',
      },
      {
         fieldName: 'nonResidenceGeographic995',
         categories: [28],
         prefix: 'nonResGeo995',
      },
      { fieldName: 'andTable995', categories: [4, 11, 12], prefix: 'and995' },
      {
         fieldName: 'betweenTable995',
         categories: [4, 11, 12],
         prefix: 'between995',
      },
      { fieldName: 'viaTable995', categories: [4], prefix: 'via995' },
      {
         fieldName: 'toGeoSpecification995',
         categories: [6, 7],
         prefix: 'toGeo995',
      },
      {
         fieldName: 'fromGeoSpecification995',
         categories: [6, 7],
         prefix: 'fromGeo995',
      },
      { fieldName: 'gatewayTable995', categories: [8], prefix: 'gateway995' },
      {
         fieldName: 'depositGeographic995',
         categories: [29],
         prefix: 'deposit995',
      },
      {
         fieldName: 'refundGeographic995',
         categories: [29],
         prefix: 'refund995',
      },
   ];

   nested995FieldConfig.forEach(({ fieldName, categories, prefix }) => {
      if (categories.includes(categoryNumber)) {
         nestedKeyMap[fieldName] = {
            [`${prefix}LocationType`]: 'locationType',
            [`${prefix}Location1Code`]: 'location1Code',
            [`${prefix}Location2Code`]: 'location2Code',
            [`${prefix}TravelSegmentIndicator`]: 'travelSegmentIndicator',
         };
      }
   });

   return nestedKeyMap;
};

export const createRecord3DetailsTableData = (record3TableData) => {
   const updatedRecord3TableData = { ...record3TableData };
   const nestedKeysToExtract = getNestedFields(record3TableData.categoryNumber);
   let nestedSegmentData = [];
   let nestedSegmentName = '';

   if (
      !RECORD_3_CATEGORIES_WITH_NESTED_FIELDS.includes(
         record3TableData.categoryNumber
      )
   ) {
      for (const [field, mappings] of Object.entries(nestedKeysToExtract)) {
         if (typeof record3TableData[field] === 'object') {
            for (const [key, nestedKey] of Object.entries(mappings)) {
               if (record3TableData[field][nestedKey]) {
                  updatedRecord3TableData[key] =
                     record3TableData[field][nestedKey];
               }
            }
            delete updatedRecord3TableData[field];
         }
      }
   } else {
      nestedSegmentName = NESTED_RECORD_3_ARRAYS.find(
         (segment) => segment.category === record3TableData.categoryNumber
      )?.fieldName;
      nestedSegmentData = record3TableData[nestedSegmentName];
      delete updatedRecord3TableData[nestedSegmentName];
   }

   const resultantRecord3TableData = nestedSegmentData.map((rowData, index) => {
      for (const key in rowData) {
         const updatedKeyName = appendCamelCaseStrings(nestedSegmentName, key);
         if(updatedKeyName !== key) {
            rowData[updatedKeyName] = rowData[key];
            delete rowData[key];
         }
      }

      if (index === 0) {
         return {
            ...updatedRecord3TableData,
            ...rowData,
         };
      } else {
         return { ...rowData };
      }
   });

   return resultantRecord3TableData.length > 0
      ? resultantRecord3TableData
      : [updatedRecord3TableData];
};

export function getRecord3TableData(record3TableData) {
   if (!record3TableData) return [];
   return createRecord3DetailsTableData(record3TableData);
}

export function validateReceivedDateFormat(searchQuery) {
   let isValidReceivedDate = true;

   for (const key in searchQuery) {
      if (Array.isArray(searchQuery[key])) {
         searchQuery[key].forEach((record) => {
            if (record.key === 'transmissionDateTime') {
               isValidReceivedDate = moment(
                  record.value,
                  DateUtil.DATE_FORMATS.DD_MMM_YY_TIME,
                  true
               ).isValid();
            }
         });
      }
   }

   return isValidReceivedDate;
}
